<template>
  <wizard-section
    :open="open"
    :show-summary="showSummary"
    class="dates-wizard"
    :class="{ 'no-dates': !hasDates }"
    @apply="apply()"
    @cancel="$emit('cancel')"
    @open="doOpen()"
  >
    <template #summary>
      <dates-wizard-summary-desktop
        v-if="$isDesktop"
        :key="'dates-wizard-summary-desktop'"
        :dates="dates"
      />
      <dates-wizard-summary
        v-else
        :key="'dates-wizard-summary'"
        :dates="dates"
        :required="required"
        :highlight-errors="highlightErrors"
      />
    </template>

    <template #body>
      <client-only>
        <dates-wizard-body
          :key="`dates-wizard-body-${slug}-${showMonths}`"
          :dates="datesModel"
          :opening-dates="openingDates"
          :availability="availability"
          :category-details="categories"
          :show-months="showMonths"
          @change-dates="changeDates($event)"
          @clearDates="$emit('clearDates')"
        >
          <template #categories="{ showingArrivalDays, selectionTagClass }">
            <dates-wizard-keys
              v-if="availability"
              :categories="categories"
              :should-show-arrival-days="showingArrivalDays"
              :selection-tag-class="selectionTagClass"
            />
          </template>
        </dates-wizard-body>
      </client-only>
    </template>
  </wizard-section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { Nullable } from '~/utility/types/Nullable'
import { ArriveDepartIso } from '../../../search/types'
import { AvailabilityResults } from '../../availability/schemas'
import { OpeningDatesRange } from '../../types'
import WizardSection from '../generic/WizardSection.vue'

@Component({
  components: {
    WizardSection,
  },
  emits: ['open', 'cancel', 'changeDates', 'clearDates'],
})
class DatesWizard extends Vue {
  @Prop()
  slug?: boolean

  @Prop({ required: true, type: Boolean })
  open: boolean

  @Prop()
  dates?: ArriveDepartIso

  @Prop()
  categories?: CampsiteCategory[]

  @Prop()
  openingDates?: OpeningDatesRange[]

  @Prop()
  availability?: AvailabilityResults

  @Prop({ required: true, type: Boolean })
  showSummary: boolean

  @Prop({ default: false })
  required?: boolean

  @Prop({ default: false })
  highlightErrors: boolean

  @Prop({ default: 1 })
  showMonths: number

  datesModel: Nullable<ArriveDepartIso> = { arrive: null, depart: null }

  created() {
    this.populateModel()
  }

  populateModel() {
    this.datesModel = this.dates
      ? { ...this.dates }
      : { arrive: null, depart: null }
  }

  changeDates(newDates: Nullable<ArriveDepartIso>) {
    this.datesModel = { ...newDates }
  }

  apply() {
    this.$emit('changeDates', this.datesModel)
  }

  doOpen() {
    this.populateModel()
    this.$emit('open')
  }

  get hasDates() {
    return this.dates && this.dates.arrive && this.dates.depart
  }
}

export default toNative(DatesWizard)
</script>
